import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { processRead, readField, writeField } from "../utils/objectUtilities";
import { dynamicFilter, dynamicSort, makeGridHeader } from "../utils/tableUtilities";
import Button from "./Button";
import Checkbox from "./Checkbox";
import CustomForm from "./CustomForm";
import { DefaultButton } from "./DefaultButton";
import { Col, Row } from './Grid';
import Icon from "./Icon";
import InfiniteScroll from "./InfiniteScroll";
import { CustomCtrl, customFieldToString } from './CustomCtrl';
import { CustomColumnDef, CustomCtrlTypeDropdown } from './CustomCtrlDef';
import TextInput from './TextInput';
import ReactForm from './ReactForm';
import FieldSelectDialog from './FieldSelectDialog';
import { ButtonGroup, Dropdown, DropdownButton } from 'react-bootstrap';
import { csvEncode } from '../utils/csvUtils';
import useNetwork from '../hooks/networkHook';
import { getSelector } from '../utils/generalUtils';
import { withRealizationScope } from '../hooks/initHook';
import { useRealizationContext } from '../reducers/utilities';
import { withComponentCapabilities } from './capabilities/componentCapabilities';
import Card from './Card';
import { ReactNodeDialog } from '../utils/dialogUtlities';
import TextareaInput from './TextareaInput';
import { CustomValidator } from './CustomValidator';
import CSVUploadButton from './CSVUploadButton';
import { ensureArray } from '../utils/arrayUtilities';
    
const GridInfiniteScoll = ({ rows, columnCount, emptyMessage, renderRow, batchSize = 20 }) => {
    const [displayRows, setDisplayRows] = useState([]);
    const observer = useRef(null);
    const loadingRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
         setDisplayRows(displayRows => rows.slice(0, Math.max(displayRows.length, batchSize)).map((row, idx) => renderRow(row, idx)));
    }, [rows, batchSize, renderRow])

    // Intersection Observer callback
    const handleObserver = useCallback(entries => {
        const target = entries[0];
        if (target.isIntersecting) {
            setDisplayRows(displayRows => {
                if (displayRows.length >= rows.length) {
                    return displayRows;
                } else if (batchSize === Infinity) {
                    return displayRows.map((row, idx) => renderRow(row, idx));
                } else {
                    return [...displayRows].concat(rows.slice(displayRows.length, displayRows.length + batchSize).map((row, idx) => renderRow(row, idx + displayRows.length)));
                }
            })
        }
    }, [rows, batchSize, renderRow]);

    useEffect(() => {
        if (loadingRef.current) {
            observer.current = new IntersectionObserver(handleObserver, {
                root: containerRef.current, // assuming viewport as root
                rootMargin: '10px',
                threshold: 0.1
            });
            observer.current.observe(loadingRef.current);
        }

        return () => {
            if (observer.current) observer.current.disconnect();
        };
    }, [loadingRef, handleObserver]);

    // TODO recheck if Chrome fixes its sizing problem when the Y scroll comes in which makes an X scroll show up - oops!
    return <div ref={containerRef} className="gridTableBody" style={{ gridColumn: `1 / ${columnCount + 1}`, overflowY: 'scroll', overflowX: 'hidden', maxWidth: '100%', maxHeight: '100%',  scrollbarGutter: 'stable'}}>
        { displayRows?.length > 0 ? displayRows.flat() : (emptyMessage && <div style={{ gridColumn: `1 / ${columnCount + 1}`}}>{emptyMessage}</div>) }
        { displayRows.length < rows.length ? <div ref={loadingRef} style={{ gridColumn: `1 / ${columnCount + 1}`, borderTop: 'none'}}></div> : null }
    </div>
}
GridInfiniteScoll.propTypes = {
    rows: PropTypes.array,
    columnCount: PropTypes.number.isRequired,
    batchSize: PropTypes.number,
    emptyMessage: PropTypes.string,
    renderRow: PropTypes.func.isRequired
}

export const customCtrlTypeSupported = ctrl => ctrl.type !== 'signature' && ctrl.type !== 'image' && ctrl.type !== 'computed' && ctrl.type !== 'list' && ctrl.type !== 'table';

export const formDefToSimpleColumns = (def, sectionPrefix) => {
    const columns = def.flatMap(section => section.rows.flat().map(cell => ({ ...cell, section: section.title })))
        .filter(cell => cell.field && cell.type !== 'signature' && customCtrlTypeSupported(cell.type))
        .map(cell => {
            let section = cell.section;
            if (section && sectionPrefix) {
                section = sectionPrefix + ' - ' + section;
            }
            let title = cell.searchLabel ?? ((typeof cell.label === 'object' && !Array.isArray(cell.label) && cell.label !== undefined) ? cell.label.default : cell.label);
            if (!title && cell.section) {
                title = cell.section;
            }
            if (title && section && title === section) {
                section = null;
            }
            return { ...cell, type: cell.type, field: cell.field, title, section }
        });
    return columns;
}

/**
 * An editor used to modify the defintion of a DataTable in a custom form layout
 * @component
 */
const DataTableEditor = ({ tools, value, onChange }) => {
    const { t } = useTranslation();
    const [newColumn, setNewColumn] = useState();
    const newFormRef = useRef();
    const def = useMemo(() => [{
        borderless: true,
        rows: [
            ...tools.labelFields(t, { searchable: false }),
            ...tools.fieldFields(t),
            [{ type: 'checkbox', field: 'addDialog', label: t('addDialog') }],
            ...tools.commonFields(t),
            [
                { type: 'checkbox', field: 'disableAdd', label: t('disableAdd') },
                { type: 'checkbox', field: 'disableRemove', label: t('disableRemove') },
                { type: 'checkbox', field: 'confirmDelete', label: t('confirmDelete') },
                { type: 'checkbox', field: 'enableUpload', label: t('enableUpload') },
            ],
            [ { type: 'text', field: 'emptyMessage', label: t('emptyMessage') } ],
            [ { type: 'json', field: 'sort', label: t('defaultSortJSON') } ],
        ]
    }],[t, tools]);

    const handleColumnUpdate = useCallback((delta, idx) => {
        const columns = [ ...(value?.columns ?? []) ];
        columns[idx] = { ...columns[idx], ...delta };
        onChange({ columns });
    }, [value, onChange]);

    const handleColumnRemove = useCallback(idx => {
        const columns = [ ...(value?.columns ?? []) ];
        columns.splice(idx, 1);
        onChange({ columns });
    }, [value, onChange]);

    return <>
        { newColumn && <ReactNodeDialog showModal={!!newColumn} size="lg" onClose={() => setNewColumn()} onSave={() => {
            if (newFormRef.current?.checkValidity()) {
                onChange({ columns: [ ...(value?.columns ?? []), newColumn ]});
                setNewColumn();
            }
        }}>
            <ReactForm ref={newFormRef}>
                <Row>
                    <Col>
                        <CustomCtrlTypeDropdown value={newColumn.type} placeholder="Control Type" onChange={type => setNewColumn(newColumn => ({ ...newColumn, type }))}></CustomCtrlTypeDropdown>
                    </Col>
                </Row>
                {newColumn.type && <Row>
                    <Col>
                        <CustomColumnDef kind={newColumn.type} value={newColumn} onChange={delta => setNewColumn(newColumn => ({ ...newColumn, ...delta }))}></CustomColumnDef>
                    </Col>
                </Row>}
            </ReactForm>
        </ReactNodeDialog> }
        <CustomForm def={def} live={true} data={value} restricted={false} disabled={false} required={true} hideLabel={false} readonly={false} onChange={onChange}></CustomForm>
        <Card title={t('columns')} headerControls={<DefaultButton kind="add" onClick={() => setNewColumn({ })}></DefaultButton>}>
            {value?.columns?.length > 0 ?
                value?.columns?.map((col, idx) => <Row key={idx} style={{ borderBottom: idx > 0 ? '1px solid grey' : 'none' }}>
                    <Col xs="auto" className="align-self-center">
                        <span className="fw-bold">{idx + 1}.</span>{' '}
                        <span className="small text-muted">{col.title || col.label || col.field || col.type}</span>
                    </Col>
                    <Col xs>
                        { col.type === 'form' || col.type === 'dynamic' ?
                            <TextareaInput value={JSON.stringify(col, null, 2)} rows={4} onChange={value => handleColumnUpdate(JSON.parse(value), idx)}></TextareaInput>
                            : <CustomColumnDef kind={col.type} value={col} onChange={delta => handleColumnUpdate(delta, idx)}></CustomColumnDef>}
                    </Col>
                    <Col xs="auto" className="align-self-start">
                        <DefaultButton kind="delete" hideLabel variant="outline-danger" onClick={() => handleColumnRemove(idx)}></DefaultButton>
                    </Col>
                </Row>)
                : t('noColumns') }
        </Card>
    </>
}

export const customCtrlDef = {
    def: () => ({
        control: DataTableEditor
    }),
    control: ({ def, value, style, ...props }, { args }) => {
        return <DataTable columns={def.columns} defaultSort={def.sort} args={args} emptyMessage={def.emptyMessage} confirmDelete={def.confirmDelete} value={value} disableAdd={def.disableAdd} disableRemove={def.disableRemove} addDialog={def.addDialog} label={def.label} style={{ maxHeight: '350px', ...def.style, ...style }} enableUpload={def.enableUpload} {...props}></DataTable>;
    },
    comparator: async (query, value, { def, compare }) => {
        const searchColumns = def.columns.filter(col => readField(query?.value, col.field) !== undefined);
        const checkEntry = async (entry) => {
            for (const col of searchColumns) {
                if (!(await compare(col, processRead(query?.value, col.field), processRead(entry, col.field)))) {
                    return false
                }
            }
            return true;
        }
        for (const entry of ensureArray(value?.value)) {
            if (await checkEntry(entry)) {
                return true;
            }
        }
        return false;
    }
};

const rowRenderButtonsStyling = {
    display: 'grid',
    gap: '4px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(40px, auto))',
    justifyContent: 'end',
    minWidth: '100%',
    alignItems: 'center'
}
/**
 * A dynamically generated table of controls for sorting and rendering data in a tabular format with one row of controls per entry.
 * @component
 */
const DataTable = withComponentCapabilities(({ columns: inputColumns, footerColumns, additionalColumns: inputAdditionalColumns, defaultSort, value: inputValue, selections, args, context, required = false, disabled = false, readonly = false, onChange, disableAdd = false, disableRemove = false, addDialog = false, label, onNewUpdate, onAdd, onUpdate, onRemove, canAdd, canUpdate, canRemove, pkey, onSelect, onRowClick, onSortChanged, getRowStyle, rowControls, hideHeader = false, emptyMessage, newDefault, confirmDelete = false, customExporters, csvFilename, style, showFilter = false, headerCtrls, selectedFields: inputSelectedFields, onFieldSelect, enableUpload = false }) => {
    const value = useMemo(() => inputValue && Array.isArray(inputValue) ? inputValue : [], [inputValue]);
    const { t, i18n } = useTranslation();
    const { t: tCommon } = useTranslation('common')
    const network = useNetwork();
    const realizationContext = useRealizationContext();
    const [sort, setSort] = useState(defaultSort ?? []);
    const [newRow, setNewRow] = useState(newDefault ?? {});
    const [selectedFields, setSelectedFields] = useState({ ...inputSelectedFields }); // map of selector (field or first element/value in field if it is an array/object) to true/false
    const [showFieldsDialog, setShowFieldsDialog] = useState();
    const [filter, setFilter] = useState();
    const [selected, setSelected] = useState({...selections});
    const [showAddDialog, setShowAddDialog] = useState(false);
    const footerRef = useRef();
    const footerRefMin = useRef();
    const dialogRef = useRef();

    const handleSortChanged = useCallback((sort) => {
        setSort(sort);
        onSortChanged?.(sort);
    }, [onSortChanged]);

    const checkNonUniqueUuids = (cols, origin) => {
        if (!cols || cols.length === 0) return;
        const seen = {};

        cols.forEach(col => {
            const selector = getSelector(col);
            if (!seen[selector]) {
                seen[selector] = [col];
            } else {
                seen[selector].push(col);
            }

        });

        const duplicates = Object.values(seen).filter(dups => dups.length > 1);

        if (duplicates.length > 0) {
            console.error(`Duplicate uuids found in ${origin}:`);
            duplicates.forEach(d => {
                console.error(d);
            });
            throw new Error(`Duplicate column uuids detected in ${origin}.`);
        }
    };

    const columns = useMemo(() => inputColumns.map(col => {
        return { ...col, selector: getSelector(col)};
    }), [inputColumns])    

    const additionalColumns = useMemo(() => inputAdditionalColumns?.map(col => {
        return { ...col, selector: getSelector(col)};
    }), [inputAdditionalColumns]);

    useEffect(() => {
        if (false && process.env.NODE_ENV === 'development') {
            const combined = [...inputColumns, ...inputAdditionalColumns ?? []];
            checkNonUniqueUuids(combined, 'columns + additionalColumns');
        }
    }, [inputColumns, inputAdditionalColumns]);

    useEffect(() => {
        setSelectedFields({ ...inputSelectedFields });
    }, [inputColumns, inputSelectedFields]);

    useEffect(() => {
        const coreSelection = columns.reduce((obj, col) => { obj[getSelector(col)] = true; return obj; }, {});
        setSelectedFields(selected => {
            if (Object.keys(selected).length > 0) {
                return selected;
            } else {
                return { ...coreSelection };
            }
        });
    }, [columns])

    if (onChange && (onAdd || onUpdate || onRemove)) {
        throw new Error("onChange and row events onAdd / onUpdte / onRemove are mutually exclusive - use one or the other");
    }

    if (onSelect && !pkey) {
        throw new Error("pkField must be specified if using onSelect");
    }

    const displayRows = useMemo(() => {
        let displayRows = [...value];
        if (displayRows?.length > 0) {
            if (showFilter && filter?.length > 0) {
                displayRows = dynamicFilter(displayRows, filter);
            }
            displayRows?.sort(dynamicSort(sort));
        } else {
            displayRows = [];
        }
        setNewRow(newDefault ?? {})
        return displayRows;
    }, [value, sort, filter, showFilter, newDefault]);

    const displayColumns = useMemo(() => {
        return columns.concat(additionalColumns ?? []).filter(c => selectedFields[c.selector])
    }, [columns, additionalColumns, selectedFields]);

    const footerDisplayColumns = useMemo(() => {
        return footerColumns ? footerColumns.filter(c => selectedFields[getSelector(c)]) : displayColumns;
    }, [footerColumns, displayColumns, selectedFields]);

    useEffect(() => {
        // need to handle type conversion, key may be an integer or a string depending on the pkField
        // eslint-disable-next-line eqeqeq
        onSelect?.(Object.keys(selected).filter(key => selected[key] && displayRows.find(row => row[pkey] == key)));
    }, [displayRows, selected, onSelect, pkey])

    const allRowsSelected = useMemo(() => displayRows.every(row => selected[row[pkey]]), [displayRows, selected, pkey]);

    const exportCSV = useCallback(async () => {
        withRealizationScope(realizationContext, async () => {
            let csv = displayColumns.map(def => csvEncode(def.title)).join(',') + '\n';
            csv += (await Promise.all(displayRows?.map(async row => (await Promise.all(displayColumns.map(async col => {
                switch (col.type) {
                    case 'form':
                        return csvEncode('<form>');
                    case 'dynamic':
                        return csvEncode(col.toString(row));
                    default:
                        return csvEncode(await customFieldToString(col, row, realizationContext, '', i18n.resolvedLanguage, args))
                }
            }))).join(',')))).join('\n');
            network.downloadBlob(new Blob([csv], { type: 'text/csv' }), csvFilename);
        })
    }, [realizationContext, displayColumns, displayRows, i18n, network]);

    const handleUpdate = useCallback((idx, field, v) => {
        if (onUpdate) {
            onUpdate(displayRows[idx], field, v)
        } else {
            const row = displayRows[idx];
            const updated = { ...row };
            writeField(updated, field, v);
            onChange(value.map(v => v !== row ? v : updated));
        }
    }, [onUpdate, onChange, displayRows, value]);

    const deleteRow = useCallback((idx) => {
        if (onRemove) {
            onRemove(displayRows[idx]);
        } else {
            const toRemove = displayRows[idx];
            onChange(value.filter(row => row !== toRemove));
        }
    }, [onRemove, onChange, value, displayRows]);

    const addRow = useCallback(() => {
        if (onAdd) {
            onAdd(newRow);
        } else {
            onChange([...value, newRow]);
        }
        setNewRow(newDefault ?? {});
    }, [onAdd, onChange, value, newRow, newDefault]);

    const handleUpload = useCallback(data => {
        if (onAdd) {
            for (const entry of data) {
                onAdd(data);
            }
        } else {
            onChange([ ...value, ...data ]);
        }
    }, [onAdd, onChange, value]);

    const updateNewRow = (field, value) => {
        if (onNewUpdate) {
            setNewRow(newRow => onNewUpdate(newRow, field, value));
        } else {
            setNewRow(newRow => {
                let update = { ...newRow };
                writeField(update, field, value);
                return update;
            });
        }
    }

    const doRender = useCallback((col, obj, onChange) => {
        switch (col.type) {
            case 'form': {
                const value = readField(obj, col.field);
                let def = col.def;
                if (typeof def === 'function') {
                    def = def(obj, disabled, true, onChange, onUpdate);
                }
                return <CustomForm key={def.uuid} def={def} data={value} args={readField(args, col.field)} context={context} disabled={disabled || !col.editable} readonly={readonly} required={true} onChange={onChange}></CustomForm>;
            }
            case 'dynamic': return col.def(obj, disabled, true, onChange, onUpdate);
            default: {
                return <CustomCtrl key={col.uuid} hideLabel def={col} value={obj} args={readField(args, col.field)} context={context} disabled={disabled || !col.editable} required={col.required} readonly={readonly} onChange={onChange}></CustomCtrl>;
            }
        }
    }, [args, disabled, onUpdate, readonly, context])

    const renderCell = useCallback((row, idx, col) => {
        const c = { ...col };
        if (!onUpdate && !onChange) {
            c.readonly = true;
        }
        if (canUpdate && !canUpdate(row)) {
            c.editable = false;
        }
        return doRender(c, row, (value, field) => handleUpdate(idx, field ?? col.field, value));
    }, [doRender, handleUpdate, onChange, canUpdate, onUpdate])
    const renderFooterCell = (col) => doRender({ ...col, editable: true }, newRow, (value, field) => {
        updateNewRow(field ?? col.field, value)
    });

    const showAdd = useMemo(() => !disabled && !disableAdd && !readonly && (!!onAdd || !!onChange) && (footerColumns || columns).every(col => !col.required || selectedFields[getSelector(col)]), [disabled, disableAdd, readonly, onAdd, onChange, footerColumns, columns, columns, selectedFields]);
    const showUpload = useMemo(() => showAdd && footerDisplayColumns.every(f => f.type !== 'form' && f.type !== 'dynamic'), [showAdd, footerDisplayColumns]);
    const showFooter = useMemo(() => showAdd || (Array.isArray(footerColumns) && footerDisplayColumns?.length > 0), [showAdd, footerColumns, footerDisplayColumns]);
    const showRemove = useMemo(() => !disabled && !disableRemove && !readonly && (!!onRemove || !!onChange), [disabled, disableRemove, readonly, onRemove, onChange]);
    const showButton = useMemo(() => showAdd || showRemove || !!rowControls, [rowControls, showAdd, showRemove]);
    const columnCount = useMemo(() => displayColumns.length + (showButton ? 1 : 0) + (onSelect ? 1 : 0), [displayColumns, showButton, onSelect]);
    let tableHeader = null;
    if (!hideHeader) {
        tableHeader = makeGridHeader(displayColumns, sort, handleSortChanged);
        if (showButton) {
            tableHeader.push(<div></div>);
        }
        if (onSelect) {
            tableHeader.unshift(<div><Checkbox hideLabel checked={allRowsSelected} onChange={checked => handleSelectAll(checked)}></Checkbox></div>)
        }
    }

    const handleAdd = useCallback(() => {
        if (footerRef.current?.checkValidity() || footerRefMin.current?.checkValidity()) {
            if (onRowClick) {
                onRowClick({ ...newDefault, ...newRow });
            } else {
                addRow()
            }
        }
    }, [addRow, newDefault, newRow, onRowClick]);

    const handleDialogAdd = useCallback(() => {
        if (dialogRef.current?.checkValidity()) {
            addRow();
            setShowAddDialog(false);
        }
    }, [addRow]);

    const handleSelect = useCallback((row, checked) => setSelected(selected => ({ ...selected, [row[pkey]]: checked})), [pkey])

    const handleSelectAll = useCallback(checked => setSelected(selected => {
        let newSelected = { ...selected };
        displayRows.forEach(row => newSelected[row[pkey]] = checked);
        return newSelected;
    }), [pkey, displayRows]);

    const renderRow = useCallback((row, id) => {
        const cols = displayColumns.map(col => {
            let colStyle = col.style?.(row) ?? [];
            return <div key={id + '_' + getSelector(col)} style={{ cursor: onRowClick ? 'pointer': null, ...colStyle, ...getRowStyle?.(row, disabled) }} onClick={onRowClick ? ((event) => onRowClick(row, event)) : undefined}>{renderCell(row, id, col)}</div>
        })
        const getDeleteMessage = () => {
        if(confirmDelete){
            switch (typeof confirmDelete) {
                case 'function':
                    return confirmDelete(row, id);
                case 'boolean':
                    return confirmDelete ? t('confirmDeleteItem', { item: row.id }): null;
                default:
                    throw new Error('confirmDelete must be a function or a boolean');
            }
        }
    };
        if (showRemove || (showFooter && showAdd) || rowControls) {
            cols.push(<div style={rowRenderButtonsStyling}>
                {rowControls && rowControls?.(row)}
                {showRemove ? <DefaultButton hideLabel variant="outline-danger" kind="delete" confirmMessage={getDeleteMessage()} disabled={canRemove && !canRemove(row)} onClick={() => deleteRow(id)}></DefaultButton> : null}
            </div>)
        }

        if (onSelect) {
            cols.unshift(<div><Checkbox checked={!!selected[row[pkey]]} hideLabel onChange={checked => handleSelect(row, checked)}></Checkbox></div>)
        }

        return cols;
    }, [showRemove, showAdd, showFooter, displayColumns, disabled, getRowStyle, onRowClick, renderCell, confirmDelete, t, deleteRow, canRemove, selected, handleSelect, onSelect, pkey, rowControls]);

    let tableFooter = null;
    if (showFooter) {
        tableFooter = addDialog ? [] : displayColumns.map(col => {
            const footerCol = footerDisplayColumns.find(fc => getSelector(fc) === getSelector(col));
            return <div key={getSelector(col)}>{footerCol ? renderFooterCell(footerCol) : null}</div>;
        });
        if (showButton) {
            tableFooter.push(<div style={{ ...rowRenderButtonsStyling, gridColumn: columnCount }}>
                {showAdd && <DefaultButton kind={addDialog ? "new" : "add"} variant="outline-primary" hideLabel disabled={(canAdd && !canAdd({ ...newDefault, ...newRow }))} onClick={addDialog ? () => setShowAddDialog(true) : handleAdd}></DefaultButton>}
                {showUpload && enableUpload && <CSVUploadButton columns={footerDisplayColumns} variant="outline-primary" hideLabel onClick={handleUpload}></CSVUploadButton>}
            </div>)
        }
    }

    const filterCtrl = showFilter ? <TextInput live={true} hideLabel placeholder={t('filter', { count: 1 })} prependText={<Icon intent="filter"></Icon>} value={filter} onChange={setFilter}></TextInput> : null;
    const headerControls = <Row className="mb-2">
        <Col>
            {filterCtrl}
        </Col>
        <Col xs="auto">
            {additionalColumns && <Button className="mx-2" variant="default" onClick={() => setShowFieldsDialog(true)}><Icon variant="solid" kind="list-check"></Icon> {t('field_other')}</Button>}
        </Col>
        <Col xs="auto">
            {(csvFilename || customExporters?.length > 0) ? <ButtonGroup>
                <DropdownButton as={ButtonGroup} variant="default" title={t('export')}>
                    {csvFilename ? <Dropdown.Item onClick={exportCSV}>{t('commaSeparatedValues')}</Dropdown.Item> : null}
                    {customExporters?.map(exporter => <Dropdown.Item key={exporter.label} onClick={() => exporter.export(realizationContext, displayColumns, displayRows)}>{exporter.label}</Dropdown.Item>)}
                </DropdownButton>
            </ButtonGroup> : null}
        </Col>
        {headerCtrls && <Col xs="auto" className="mx-2">
            {headerCtrls}
        </Col> }
    </Row>

    const handleFieldSelectSave= useCallback((selected) => {
        setSelectedFields(selected); 
        onFieldSelect?.({ ...selected });
        setShowFieldsDialog(false);
    }, [onFieldSelect])

    return <>
    <FieldSelectDialog showModal={!!showFieldsDialog} columns={columns} additionalColumns={additionalColumns} selectedFields={selectedFields}
        onClose={() => setShowFieldsDialog(false)} onSave={handleFieldSelectSave}></FieldSelectDialog>
    { addDialog && <ReactNodeDialog title={label ? `${t('new')} ${label}` : t('newRow')} showModal={showAddDialog} size="lg" onSave={handleDialogAdd} onClose={() => { setNewRow(newDefault ?? {}); setShowAddDialog(false); }}>
        <ReactForm ref={dialogRef}>
            {footerDisplayColumns.map(col => <Row key={getSelector(col)} className="mb-2">
                {col.title ? <Col xs={3}>{col.title}:</Col> : null}
                <Col xs={col.title ? 9 : 12}>{renderFooterCell(col)}</Col>
            </Row>)}
        </ReactForm>
    </ReactNodeDialog> }

    <div className="d-none d-md-flex flex-column px-2" style={{ maxHeight: '100%', width: '100%', ...style }}>
        {headerControls}
        <div style={{ display: 'grid', gridTemplateColumns: showButton ? `repeat(${columnCount-1}, auto) minmax(120px, auto)` : `repeat(${columnCount}, auto)`, gridTemplateRows: 'auto auto 1fr', flex: 1, width: '100%', overflow: 'auto', ...style }}>
            { tableHeader && <div className="gridTableHeader mt-2" style={{ gridColumn: `1 / ${columnCount + 1}` }}>
                {tableHeader}
            </div> }
            <GridInfiniteScoll rows={displayRows} renderRow={renderRow} emptyMessage={emptyMessage} columnCount={columnCount}></GridInfiniteScoll>
            { showFooter && <ReactForm ref={footerRef} className="gridTableHeader" style={{ gridColumn: `1 / ${columnCount + 1}`, scrollbarGutter: 'stable', paddingRight: '17px', }}>
                {tableFooter}   
            </ReactForm> }
        </div>
    </div>
    <div className="d-flex d-md-none flex-column" style={{maxHeight: '100%', ...style}}>
        {headerControls}
        {onSelect && <Checkbox hideLabel checked={allRowsSelected} onChange={checked => handleSelectAll(checked)}></Checkbox>}
        {displayRows.length === 0 ? <div>{emptyMessage}</div> : <InfiniteScroll style={{flex: 1}} data={displayRows} renderItem={(row, idx) => <Row key={idx + '_row'} className="" style={{ backgroundColor: idx % 2 ? null : '#f9f9f9' }}>
            {onSelect && <Col xs="auto" className="py-2"><Checkbox checked={selected[row[pkey]]} hideLabel onChange={checked => handleSelect(row, checked)}></Checkbox></Col>}
            <Col className="py-2">
                <div onClick={onRowClick ? (() => onRowClick(row)) : undefined}>
                    {displayColumns.map(col => {
                        let colStyle = col.style?.(row) ?? undefined;
                        return <Row key={idx + '_' + getSelector(col)} className="mb-2" >
                            {col.title ? <Col xs={3}>{col.title}:</Col> : null}
                            <Col xs={col.title ? 9 : 12} style={colStyle}>{renderCell(row, idx, col)}</Col>
                        </Row>
                })}
                </div>
                {showRemove || rowControls ? <Row><Col className="text-end">
                     {rowControls?.(row)} 
                     {showRemove && <DefaultButton kind="delete" variant="outline-danger" hideLabel confirmMessage={confirmDelete ? t('confirmDeleteItem', { item: idx }) : null} disabled={canRemove && !canRemove(row)} onClick={() => deleteRow(idx)}></DefaultButton>}
                </Col></Row> : null} 
              
            </Col>
        </Row>
        }></InfiniteScroll>}
        {showFooter && <ReactForm ref={footerRefMin}>
            <Row style={{ backgroundColor: displayRows?.length % 2 ? null : '#f9f9f9' }}>
                <Col className="py-2">
                    {!addDialog && footerDisplayColumns.map(col => <Row key={getSelector(col)} className="mb-2">
                        {col.title ? <Col xs={3}>{col.title}:</Col> : null}
                        <Col xs={col.title? 9 : 12}>{renderFooterCell(col)}</Col>
                    </Row>)}
                    <Row>
                        <Col className="text-end">
                            { showAdd && <DefaultButton kind={addDialog ? "new" : "add"} variant="outline-primary" hideLabel disabled={!addDialog && ((footerColumns ?? columns).filter(col => col.required).some(col => readField(newRow, col.field) === undefined) || (canAdd && !canAdd({ ...newDefault, ...newRow })))} onClick={addDialog ? () => setShowAddDialog(true) : handleAdd}></DefaultButton> }
                            { showUpload && enableUpload && <CSVUploadButton columns={footerDisplayColumns} variant="outline-primary" onClick={handleUpload}></CSVUploadButton> }
                        </Col>
                    </Row>
                </Col>
            </Row>
        </ReactForm>}
    </div>
    <CustomValidator value={value} onValidate={value => required && !(value?.length > 0) ? tCommon('validationEntryRequired') : false}></CustomValidator>
    </>
}, {

});

DataTable.propTypes = {
    /**
     * An array of JSON objects describing the columns of the table.
     * The schema for these objects is defined by the CustomCtrl - the only difference is
     * that label is replaced by title.
    */
   columns: PropTypes.arrayOf(PropTypes.object).isRequired,
   
    /**
        * An array of JSON objects describing the column controls for the row to add
    */
    footerColumns: PropTypes.arrayOf(PropTypes.object),
    
    /**
     * An array of JSON objects describing potential additional table columns. If specified
     * the data table will provide a message dialog enabling the user to select which additional
     * columns they want to display/export
     */
    additionalColumns:PropTypes.arrayOf(PropTypes.object),
    
    /**
     * An array of strings where each string is the name of a field for sort
     * ordering. Reverse ordering is achieved using a '-' prefix on the field name
     */
    defaultSort: PropTypes.arrayOf(PropTypes.string),

    /**
     * The array of objects whose values will be displayed and/or manipulated
     */
    value: PropTypes.array,

    /**
     * An object whose keys are the primary keys are the selected rows
     */
    selections: PropTypes.object,

    /**
     * An object of additional arguments that will be chained through to the custom controls
     * used to render the elements of the data table
     */
    args: PropTypes.object,

    /**
     * Force the input to be disabled
     */
    disabled: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    readonly: PropTypes.bool,

    /**
     * Called with the complete array of values behind the DataTable when any
     * change is made to the DataTable - you may only supply either onChange or
     * onAdd / onUpdate / onRemove
     */
    onChange: PropTypes.func,
    
    /**
     * Prohibit entires being added to the data list
     */
    disableAdd: PropTypes.bool,

    /**
     * Prohibit entires from being removed from the data list
     */
    disableRemove: PropTypes.bool,

    /**
     * When true, clicking New opens a dialog to fill in the new entry instead of an inline footer row
     */
    addDialog: PropTypes.bool,

    /**
     * Called with the object representing the row being added to the table
     */
    onAdd: PropTypes.func,

    /**
     * Called with the the row being updated, the field being updated, and the new value
     * to be stored into the field.
     */
    onUpdate: PropTypes.func,

    /**
     * Called with the row being removed
     */
    onRemove: PropTypes.func,

    /**
     * Called with the row being added, returns true if the row is valid
     */
    canAdd: PropTypes.func,

    /**
     * Called with the updated row, returns true if the update is valid
     */
    canUpdate: PropTypes.func,

    /**
     * Called with the row to remove, returns true if the row can be removed
     */
    canRemove: PropTypes.func,

    /**
     * The primary key for the objects passed in (value) - required if onSelect is specified
     */
    pkey: PropTypes.string,

    /**
     * Called when the set of selected rows changes (via filtering or checking/unchecking rows)
     */
    onSelect: PropTypes.func,
    
    /**
     * Called with the row when the row is clicked
     */
    onRowClick: PropTypes.func,

    /**
     * If supplied, called with the row and disabled value, and returns the style for the row
     */
    getRowStyle: PropTypes.func,

    /**
     * Displays custom action controls for each row
     */
    rowControls: PropTypes.func,

    /**
     * Do not show the top header row, associated labels, and sorting controls
     */
    hideHeader: PropTypes.bool,

    /**
     * The message to be displayed in place of the table body when there is no data
     */
    emptyMessage: PropTypes.string,

    /**
     * The default data to populate into the new table row
     */
    newDefault: PropTypes.object,

    /**
     * Forces prompt to confirm deletion before proceeding
     */
    confirmDelete: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),

    /**
     * An array of objects that have labels and functions which will be passed the table definition
     * and data for performing a custom export.
     */
    customExporters: PropTypes.arrayOf(PropTypes.object),

    /**
     * Filename for the csv export. Must be specified to enable csv export
     */
    csvFilename: PropTypes.string,

    /**
     * Custom styling
     */
    style: PropTypes.object,

    /**
     * When true shows a textbox above the table which allows the user to filter the displayed table rows
     */
    showFilter: PropTypes.bool,
    /**
     * A React Node which are the controls to render in the card header
     */
    headerCtrls: PropTypes.node,
    /**
     * A map of fields to true or false to indicate which fields should be selected (built from the list of columns when not supplied)
     */
    selectedFields: PropTypes.object,
    /**
     * A callback notified if the set of selected fields is updated - called with the current selection map
     */
    onFieldSelect: PropTypes.func
}

export default DataTable;